import { useState, useCallback, useRef, useEffect } from 'react';
import { X, Upload, Plus, Box, Zap, Link } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useClickOutside } from '../../hooks/useClickOutside';
import { Integration, IntegrationSetting, CreateIntegrationDto } from '../../types/integration';
import { integrationService } from '../../services/integration.service';
import ValidatedInput from '../shared/ValidatedInput';
import { DEFAULT_VALIDATION_CONFIG, validateAndSanitizeInput } from '../../utils/validation/inputValidation';

interface NewIntegrationDialogProps {
  onClose: () => void;
  onIntegrationCreated: () => void;
  isEdit?: boolean;
  initialData?: Integration | null;
}

export default function NewIntegrationDialog({
  onClose,
  onIntegrationCreated,
  isEdit = false,
  initialData = null
}: NewIntegrationDialogProps) {
  const [name, setName] = useState(validateAndSanitizeInput(initialData?.name || '', DEFAULT_VALIDATION_CONFIG.integrationName));
  const [nameError, setNameError] = useState('');
  const [category, setCategory] = useState(initialData?.category || '');
  const [description, setDescription] = useState(validateAndSanitizeInput(initialData?.description || '', DEFAULT_VALIDATION_CONFIG.integrationDescription));
  const [latencyImpact, setLatencyImpact] = useState(initialData?.latency_impact?.toString() || '50');
  const [keywords, setKeywords] = useState<string[]>(initialData?.keywords || []);
  const [newKeyword, setNewKeyword] = useState('');
  const [documentationLinks, setDocumentationLinks] = useState<Array<{ url: string; label: string }>>(() => {
    if (initialData?.documentation) {
      // Handle different documentation formats
      if (typeof initialData.documentation === 'object' && initialData.documentation !== null && 'links' in initialData.documentation) {
        return (initialData.documentation as { links: Array<{ url: string; label: string }> }).links;
      } else if (typeof initialData.documentation === 'object' && initialData.documentation !== null && 'urls' in initialData.documentation) {
        // Convert old format to new format
        const urls = (initialData.documentation as { urls: string[] }).urls;
        return urls.map((url: string) => ({ url, label: 'Documentation' }));
      } else if (Array.isArray(initialData.documentation)) {
        if (initialData.documentation.length > 0 && typeof initialData.documentation[0] === 'string') {
          // Convert string array to new format
          return (initialData.documentation as string[]).map((url: string) => ({ url, label: 'Documentation' }));
        } else {
          // Already in the correct format
          return initialData.documentation as Array<{ url: string; label: string }>;
        }
      } else if (typeof initialData.documentation === 'string') {
        // Convert single string to new format
        return [{ url: initialData.documentation, label: 'Documentation' }];
      }
    }
    return [];
  });
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('Documentation');
  const [settingsSchema, setSettingsSchema] = useState<IntegrationSetting[]>(Array.isArray(initialData?.settings_schema) ? initialData.settings_schema : []);
  const [destination, setDestination] = useState(initialData?.destination ?? true);
  const [syncInjector, setSyncInjector] = useState(initialData?.sync_injector ?? false);
  const [enabled, setEnabled] = useState(initialData?.enabled ?? true);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showNotification } = useNotifications();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEscapeKey(onClose);
  useClickOutside(onClose, [], [dialogRef]);

  useEffect(() => {
    if (initialData?.logo_url) {
      setLogoPreview(initialData.logo_url);
    }
  }, [initialData]);

  useEffect(() => {
    const predefinedCategories = [
      'Advertising',
      'Analytics',
      'Customer Data Platform',
      'Customer Engagement Platform',
      'Data Storage',
      'Data Warehouse',
      'Partnership Management',
      'Personalization'
    ];
    
    setAvailableCategories(predefinedCategories);
    
    const loadCategories = async () => {
      try {
        const categories = await integrationService.getCategories();
        const allCategories = [...new Set([...predefinedCategories, ...categories])];
        setAvailableCategories(allCategories);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    
    loadCategories();
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !keywords.includes(newKeyword.trim())) {
      setKeywords([...keywords, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setKeywords(keywords.filter(k => k !== keyword));
  };

  const validateSettings = useCallback((settings: IntegrationSetting[]): boolean => {
    return settings.every(setting => 
      setting.label.trim() !== '' && 
      setting.value.trim() !== '' && 
      Array.isArray(setting.type) && 
      setting.type.length > 0 && 
      setting.type.every(t => t === 'Destination' || t === 'Sync Injector')
    );
  }, []);

  const handleAddSetting = () => {
    setSettingsSchema([
      ...settingsSchema,
      { 
        label: '', 
        value: '', 
        type: [destination ? 'Destination' : 'Sync Injector']
      }
    ]);
  };

  const handleRemoveSetting = (index: number) => {
    setSettingsSchema(settingsSchema.filter((_, i) => i !== index));
  };

  const handleSettingChange = (index: number, field: keyof IntegrationSetting, value: any) => {
    const newSettings = [...settingsSchema];
    newSettings[index] = { ...newSettings[index], [field]: value };
    setSettingsSchema(newSettings);
  };

  const handleAddLink = () => {
    if (newLinkUrl.trim()) {
      setDocumentationLinks([...documentationLinks, { 
        url: newLinkUrl.trim(), 
        label: newLinkLabel.trim() || 'Documentation' 
      }]);
      setNewLinkUrl('');
      setNewLinkLabel('Documentation');
    }
  };

  const handleRemoveLink = (index: number) => {
    setDocumentationLinks(documentationLinks.filter((_, i) => i !== index));
  };

  const handleCreate = useCallback(async () => {
    try {
      if (!name.trim()) {
        setNameError('Integration name is required');
        return;
      }
      
      if (!category) {
        showNotification('error', 'Category is required');
        return;
      }

      if (!validateSettings(settingsSchema)) {
        showNotification('error', 'All settings must have a label, value, and valid type');
        return;
      }
      
      // Sanitize inputs before saving
      const sanitizedName = validateAndSanitizeInput(name, DEFAULT_VALIDATION_CONFIG.integrationName);
      const sanitizedDescription = validateAndSanitizeInput(description, DEFAULT_VALIDATION_CONFIG.integrationDescription);

      const newIntegration: CreateIntegrationDto = {
        name: sanitizedName,
        category,
        description: sanitizedDescription,
        logo_url: initialData?.logo_url || "",
        latency_impact: parseInt(latencyImpact),
        keywords,
        documentation: { links: documentationLinks },
        settings_schema: Array.isArray(settingsSchema) ? settingsSchema : [],
        enabled,
        display_order: initialData?.display_order || 0,
        destination,
        sync_injector: syncInjector
      };

      // Log the data being sent to the backend
      
      if (isEdit && initialData) {
        await integrationService.updateIntegration(initialData.id, newIntegration, logoFile || undefined);
      } else {
        await integrationService.createIntegration(newIntegration, logoFile || undefined);
      }
      
      showNotification('success', `Integration ${isEdit ? 'updated' : 'created'} successfully`);
      onClose();
      onIntegrationCreated();
    } catch (error) {
      console.error('Error creating integration:', error);
      showNotification('error', 'Failed to create integration');
    }
  }, [
    name,
    category,
    destination,
    syncInjector,
    description,
    latencyImpact,
    keywords,
    documentationLinks,
    settingsSchema,
    enabled,
    logoFile,
    showNotification,
    isEdit,
    initialData,
    onClose,
    onIntegrationCreated,
    validateSettings
  ]);

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-gray-700/50 flex items-center justify-center z-[200]">
      <div ref={dialogRef} className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-[640px] mx-4">
        {/* Header */}
        <div className="px-6 pt-5 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {isEdit ? 'Edit Integration' : 'New Integration'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="mb-4 space-y-4">
            {/* Integration Type Toggles */}
            <div className="flex w-full gap-4 mb-6">
              <button
                type="button"
                onClick={() => setDestination(!destination)}
                className={`flex items-center justify-center gap-2 flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                  destination
                    ? 'border-blue-600 bg-blue-50 text-blue-600 dark:border-blue-500 dark:bg-blue-900/20 dark:text-blue-400'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <Box className="h-5 w-5" />
                <span className="font-medium">Destination</span>
              </button>
              <button
                type="button"
                onClick={() => setSyncInjector(!syncInjector)}
                className={`flex items-center justify-center gap-2 flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                  syncInjector
                    ? 'border-blue-600 bg-blue-50 text-blue-600 dark:border-blue-500 dark:bg-blue-900/20 dark:text-blue-400'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <Zap className="h-5 w-5" />
                <span className="font-medium">Sync Injector</span>
              </button>
            </div>

            {/* Logo Upload */}
            <div>
              <div className="flex items-center gap-4">
                {logoPreview ? (
                  <div className="w-16 h-16 rounded-lg overflow-hidden">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-16 h-16 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                    <Upload className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Choose file
                </button>
                <p className="text-sm text-gray-500 dark:text-gray-400 ml-2 w-[280px]">
                  Please use our <a href="https://drive.google.com/drive/folders/1IOVdbhk9FAWInqVQEsaKDKLLxZuZPeyL?usp=drive_link" target="_blank" className="text-blue-600 dark:text-blue-400 hover:underline">official logo icons</a>, reach out to Mike P. if your icon is missing.
                </p>
              </div>
            </div>

            {/* Basic Information */}
            <ValidatedInput
              id="integration-name"
              label="Name *"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError('');
              }}
              validationConfig={DEFAULT_VALIDATION_CONFIG.integrationName}
              placeholder="Enter integration name"
              errorMessage={nameError}
              onBlur={() => {
                if (!name.trim()) {
                  setNameError('Integration name is required');
                }
              }}
            />

            <div>
              <label
                htmlFor="integration-category"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Category *
              </label>
              <select
                id="integration-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-200"
              >
                <option value="">Select a category</option>
                {availableCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
                <option value="Custom">Custom</option>
              </select>
            </div>

            <ValidatedInput
              id="integration-description"
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              validationConfig={DEFAULT_VALIDATION_CONFIG.integrationDescription}
              placeholder="Enter integration description"
              isTextarea={true}
              rows={3}
            />

            {/* Documentation Links */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Documentation Links
              </label>
              
              <div className="flex gap-2 mb-2">
                <div className="flex-1">
                  <input
                    type="url"
                    value={newLinkUrl}
                    onChange={(e) => setNewLinkUrl(e.target.value)}
                    placeholder="Enter URL"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-200"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddLink();
                      }
                    }}
                  />
                </div>
                <div className="w-1/3">
                  <input
                    type="text"
                    value={newLinkLabel}
                    onChange={(e) => setNewLinkLabel(e.target.value)}
                    placeholder="Label"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-200"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddLink();
                      }
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddLink}
                  className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
              
              {documentationLinks.length > 0 && (
                <div className="space-y-2 mt-2">
                  {documentationLinks.map((link, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md"
                    >
                      <Link className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <div className="flex-1 truncate">
                        <a 
                          href={link.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline truncate"
                        >
                          {link.url}
                        </a>
                      </div>
                      <div className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                        {link.label}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveLink(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Keywords
              </label>
              <div className="flex mt-1">
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Add keyword"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-200"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddKeyword();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddKeyword}
                  className="px-3 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
              {keywords.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {keywords.map((keyword) => (
                    <div
                      key={keyword}
                      className="flex items-center bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-md"
                    >
                      <span className="text-sm dark:text-gray-300">{keyword}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveKeyword(keyword)}
                        className="ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Advanced Settings */}
            <div className="flex gap-4">
              <div className="w-1/2">
                <label
                  htmlFor="latency-impact"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Latency Impact (ms)
                </label>
                <input
                  type="number"
                  id="latency-impact"
                  min="0"
                  max="100"
                  value={latencyImpact}
                  onChange={(e) => setLatencyImpact(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-200"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Setting used to calculate the latency impact of an integration on the page load times.
                </p>
              </div>
            </div>
            
            {/* Settings Schema */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex justify-between items-center w-full">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Custom Settings
                  </label>
                  <button
                    type="button"
                    onClick={handleAddSetting}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Setting
                  </button>
                </div>
              </div>
              {settingsSchema.map((setting, index) => (
                <div key={index} className="flex gap-2 items-start mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
                  <div className="flex-grow">
                    <input
                      type="text"
                      placeholder="Label"
                      value={setting.label}
                      onChange={(e) => handleSettingChange(index, 'label', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-200"
                    />
                  </div>
                  <div className="flex-grow">
                    <input
                      type="text"
                      placeholder="Value"
                      value={setting.value}
                      onChange={(e) => handleSettingChange(index, 'value', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-200"
                    />
                  </div>
                  <div className="flex-1">
                    <select
                      value={setting.type[0]}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        handleSettingChange(index, 'type', [
                          e.target.value as 'Destination' | 'Sync Injector'
                        ]);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md"
                    >
                      <option value="Destination">Dest.</option>
                      <option value="Sync Injector">Sync</option>
                    </select>
                  </div>
                  <button onClick={() => handleRemoveSetting(index)} className="text-red-500 hover:text-red-700">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 rounded-b-lg border-t border-gray-100 dark:border-gray-800">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="relative inline-block w-10 mr-2 align-middle select-none">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="sr-only"
                />
                <div className={`block h-6 rounded-full transition-colors ${enabled ? 'bg-blue-600 dark:bg-blue-700' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                <div className={`absolute left-0.5 top-0.5 bg-white dark:bg-gray-200 w-5 h-5 rounded-full transition-transform transform ${enabled ? 'translate-x-4' : ''}`}></div>
              </div>
              <label
                htmlFor="enabled"
                className="block text-sm text-gray-700 dark:text-gray-300"
              >
                Enabled
              </label>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              className={`px-4 py-2 text-sm font-medium rounded-md text-white ${
                !name || !category
                  ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800'
              }`}
            >
              {isEdit ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
